import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from './axiosInstance';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import DataGrid from './ui/DataTable';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { useTheme, alpha } from '@mui/material/styles';

import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {  
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
    Accordion,
  AccordionSummary,
  AccordionDetails,
    Divider,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Menu from '@mui/material/Menu';
import { SnackbarProvider, useSnackbar } from "../providers/SnackbarProvider";
import InlineEdit from './ui/InlineEdit';
import MilestoneTracker from './ui/MilestoneTracker';
import TabWithCounter from './ui/TabWithCounter';
import LostReasonDialog from './ui/LostReasonDialog';
import { DetailSkeleton } from './ui/PageSkeleton';
import { usePipelineConfig } from '../providers/PipelineConfigProvider';

import InteractionTimeline from './InteractionTimeline';
import { formatBytes, truncateFileName, getMimeType, getFileIcon } from "../utils/fileUtils";

// File icons
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArticleIcon from "@mui/icons-material/Article";
import UploadFileIcon from "@mui/icons-material/UploadFile";


import {
    statusOptions as contractStatusOptions
} from '../constants/contractOptions';

// Breadcrumbs
import { useBreadcrumbs } from './BreadcrumbBar';

// Themen-Fläche im v2.5-Look (wie SectionCard): weißes Papier auf getöntem Grund.
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  ...theme.typography.body2,
  padding: theme.spacing(2.5),
  textAlign: 'left',
  color: theme.palette.text.primary,
  height: '100%',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  boxShadow: '0 1px 2px rgba(20,24,31,.05), 0 1px 1px rgba(20,24,31,.03)',
}));

const ProjectDetails = () => {
  const { projectId } = useParams();
  // Pipeline-Konfiguration (B3): Phasen & Checklisten aus dem Admin
  const { phasesFor, lostFor } = usePipelineConfig();
  const projectPhases = phasesFor('project');
  const projectLost = lostFor('project');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateError, setDateError] = useState(null);
  
  const [timeEntries, setTimeEntries] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // Übersicht · Verträge · Zeiterfassung
  const [editTimeEntryDialogOpen, setEditTimeEntryDialogOpen] = useState(false);
  const [selectedTimeEntry, setSelectedTimeEntry] = useState(null);

  const [contracts, setContracts] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [expandedContract, setExpandedContract] = useState(false);

  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [filterMode, setFilterMode] = useState('month'); // 'month' | 'range'
  const theme = useTheme();

  // Breadcrumbs
  useBreadcrumbs(
    project
      ? [
          { label: 'Projekte', path: '/projects' },
          { label: `${project.account_name}: ${project.name}` },
        ]
      : null
  );

  const formatDate = (date) => {
    return dayjs(date).format('MMM D, YYYY');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const getInitials = (name) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };



    // Mapping der Status-Farben aus theme.palette
  const getStatusColor = (colorKey) => {
    switch (colorKey) {
      case 'Completed':
        return theme.palette.success.main;
      case 'Active':
        return theme.palette.info.main;
      case 'Cancelled':
        return theme.palette.error.main;
      case 'default':
      default:
        return theme.palette.grey[500];
    }
  };

  // Time Entry Filter States
  const [timeEntryFilters, setTimeEntryFilters] = useState({
    consultant: '',
    startDate: '',
    endDate: '',
    month: ''
  });

    // Contract Dialog States
    const [createContractDialogOpen, setCreateContractDialogOpen] = useState(false);
    const [editContractDialogOpen, setEditContractDialogOpen] = useState(false);
    const [selectedContract, setSelectedContract] = useState(null);
    const [newContract, setNewContract] = useState({
      title: '',
      project: projectId,
      consultant_assignments: [{ consultant: '', rate: 0, hours: 0 }],
      start_date: dayjs().format('YYYY-MM-DD'),
      end_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
      status: 'Draft',
      notes: '',
      contract_file: null
    });

  // Reset Filters
  const handleResetFilters = () => {
    setTimeEntryFilters({
      consultant: '',
      startDate: '',
      endDate: '',
      month: ''
    });
  };

  // Filter between months and range
  const handleFilterModeChange = (mode) => {
  setFilterMode(mode);
  if (mode === 'month') {
    setTimeEntryFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
  } else {
    setTimeEntryFilters(prev => ({ ...prev, month: '' }));
  }
};
  
  // Check if any filters are active
  const hasActiveFilters = () => {
    return timeEntryFilters.consultant || timeEntryFilters.startDate || timeEntryFilters.endDate || timeEntryFilters.month;
  };
    // Filter Time Entries
    const getFilteredTimeEntries = () => {
      return timeEntries.filter(entry => {
        if (timeEntryFilters.consultant && entry.consultant !== parseInt(timeEntryFilters.consultant)) {
          return false;
        }
        if (timeEntryFilters.startDate && dayjs(entry.date).isBefore(dayjs(timeEntryFilters.startDate))) {
          return false;
        }
        if (timeEntryFilters.endDate && dayjs(entry.date).isAfter(dayjs(timeEntryFilters.endDate))) {
          return false;
        }
        if (timeEntryFilters.month) {
          const [year, month] = timeEntryFilters.month.split('-');
          if (
            dayjs(entry.date).month() + 1 !== parseInt(month) ||
            dayjs(entry.date).year() !== parseInt(year)
          ) {
            return false;
          }
        }
        return true;
      });
    };




// Zeiteinträge bearbeiten
const handleOpenEditTimeEntryDialog = (entry) => {
  setSelectedTimeEntry(entry);
  setEditTimeEntryDialogOpen(true);
};

const handleCloseEditTimeEntryDialog = () => {
  setEditTimeEntryDialogOpen(false);
  setSelectedTimeEntry(null);
};

const handleSaveTimeEntry = async (id, formData) => {
  await axiosInstance.patch(`/time-entries/${id}/`, formData);
  fetchTimeEntries();
};

// Zeiteintrag löschen
const handleDeleteTimeEntry = async (id) => {
  if (!window.confirm('Möchten Sie diesen Zeiteintrag wirklich löschen?')) {
    return;
  }
  try {
    await axiosInstance.delete(`/time-entries/${id}/`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    });
    fetchTimeEntries();
  } catch (error) {
    console.error("Error deleting time entry:", error);
  }
};



      // PDF Export für Time Entries
      const handleExportTimeEntriesToPDF = () => {
        try {
          const doc = new jsPDF();
          
          // Header
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.text('Report - Zeiterfassung', 14, 20);
          
          // Project Info
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          doc.text(`Projekt: ${project.name}`, 14, 30);
          doc.text(`Unternehmen: ${project.account_name}`, 14, 36);
          doc.text(`Reportdatum: ${dayjs().format('DD.MM.YYYY')}`, 14, 42);
          
          // Add filter info if filters are applied
          let yPosition = 48;
          if (timeEntryFilters.consultant || timeEntryFilters.startDate || timeEntryFilters.endDate) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            let filterText = 'Filter: ';
            const filterParts = [];
            
            if (timeEntryFilters.consultant) {
              const consultant = consultants.find(c => c.id === parseInt(timeEntryFilters.consultant));
              const consultantName = consultant?.first_name && consultant?.last_name
                ? `${consultant.first_name} ${consultant.last_name}`
                : consultant?.email || 'Unbekannt';
              filterParts.push(`Berater: ${consultantName}`);
            }
            if (timeEntryFilters.startDate) {
              filterParts.push(`Von: ${dayjs(timeEntryFilters.startDate).format('DD.MM.YYYY')}`);
            }
            if (timeEntryFilters.endDate) {
              filterParts.push(`Bis: ${dayjs(timeEntryFilters.endDate).format('DD.MM.YYYY')}`);
            }
            
            filterText += filterParts.join(', ');
            doc.text(filterText, 14, yPosition);
            yPosition += 6;
          }
          
          // Get filtered entries
          const filteredEntries = getFilteredTimeEntries();
          const sortedEntries = [...filteredEntries].sort((a, b) => new Date(b.date) - new Date(a.date));
          
          const tableData = sortedEntries.map(entry => {
            const consultant = consultants.find(c => c.id === entry.consultant);
            const consultantName = consultant?.first_name && consultant?.last_name
              ? `${consultant.first_name} ${consultant.last_name}`
              : consultant?.email || 'Unbekannt';
            
            return [
              dayjs(entry.date).format('DD.MM.YYYY'),
              consultantName,
              `${entry.hours}h`,
              entry.description || '-'
            ];
          });
          
          // Add total hours
          const totalHours = filteredEntries.reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0);
          
          // Create table using autoTable
          autoTable(doc, {
            startY: yPosition + 2,
            head: [['Datum', 'Berater', 'Stunden', 'Beschreibung']],
            body: tableData,
            foot: [['', 'Gesamt:', `${totalHours.toFixed(2)}h`, '']],
            headStyles: {
              fillColor: [33, 150, 243],
              textColor: 255,
              fontStyle: 'bold',
              fontSize: 10
            },
            footStyles: {
              fillColor: [240, 240, 240],
              textColor: 0,
              fontStyle: 'bold',
              fontSize: 10
            },
            bodyStyles: {
              fontSize: 9
            },
            alternateRowStyles: {
              fillColor: [250, 250, 250]
            },
            margin: { top: 50 },
            styles: {
              cellPadding: 3,
              fontSize: 9,
              overflow: 'linebreak',
            },
            columnStyles: {
              0: { cellWidth: 30 },
              1: { cellWidth: 50 },
              2: { cellWidth: 25 },
              3: { cellWidth: 'auto' }
            }
          });
          
          // Footer
          const pageCount = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(
              `Seite ${i} von ${pageCount}`,
              doc.internal.pageSize.getWidth() / 2,
              doc.internal.pageSize.getHeight() - 10,
              { align: 'center' }
            );
          }
          
          // Save PDF
          const fileName = `Zeiterfassung_${project.name.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
          doc.save(fileName);
          
          showSnackbar('PDF erfolgreich exportiert', 'success');
        } catch (error) {
          console.error('Error exporting PDF:', error);
          showSnackbar('Fehler beim Exportieren des PDFs', 'error');
        }
      };

    // CSV Export für Time Entries
    const handleExportTimeEntriesToCSV = () => {
      try {
        // Get filtered entries
        const filteredEntries = getFilteredTimeEntries();
        const sortedEntries = [...filteredEntries].sort((a, b) => new Date(b.date) - new Date(a.date));
        
        // CSV Header
        const headers = ['Datum', 'Berater', 'Stunden', 'Beschreibung'];
        
        // CSV Rows
        const rows = sortedEntries.map(entry => {
          const consultant = consultants.find(c => c.id === entry.consultant);
          const consultantName = consultant?.first_name && consultant?.last_name
            ? `${consultant.first_name} ${consultant.last_name}`
            : consultant?.email || 'Unbekannt';
          
          return [
            dayjs(entry.date).format('DD.MM.YYYY'),
            consultantName,
            entry.hours,
            entry.description ? `"${entry.description.replace(/"/g, '""')}"` : '-'
          ];
        });
        
        // Add total hours row
        const totalHours = filteredEntries.reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0);
        rows.push(['', 'Gesamt:', totalHours.toFixed(2), '']);
        
        // Combine headers and rows
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.join(','))
        ].join('\n');
        
        // Add BOM for proper Excel UTF-8 encoding
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        
        // Create download link
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `Zeiterfassung_${project.name.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        showSnackbar('CSV erfolgreich exportiert', 'success');
      } catch (error) {
        console.error('Error exporting CSV:', error);
        showSnackbar('Fehler beim Exportieren der CSV', 'error');
      }
    };
    
  const handleOpenAccount = () => {
    if (project?.account) {
      navigate(`/accounts/${project.account}/`);
    }
  };


  const handleAccordionChange = (contractId) => (event, isExpanded) => {
    setExpandedContract(isExpanded ? contractId : false);
  };
  
  const handleOpenEditContractDialog = (contract) => {

    fetchAttachments(contract.id);

    setSelectedContract({
      ...contract,
      consultant_assignments: contract.consultant_assignments || 
        [{ consultant: '', rate: 0, hours: 0 }]
    });
    setEditContractDialogOpen(true);
  };

  const handleSaveContract = async () => {
    const validAssignments = selectedContract.consultant_assignments.filter(
      a => a.consultant && a.hours > 0 && a.rate > 0
    );

    if (validAssignments.length === 0) {
      showSnackbar("Mindestens ein Berater mit Stunden und Rate ist erforderlich", "error");
      return;
    }

    if (dayjs(selectedContract.end_date).isBefore(dayjs(selectedContract.start_date))) {
      showSnackbar('Enddatum muss nach Startdatum liegen', "error");
      return;
    }

    try {
      const response = await axiosInstance.put(
        `/contracts/${selectedContract.id}/`,
        {
          title: selectedContract.title,
          project: projectId,
          consultant_assignments: validAssignments,
          start_date: selectedContract.start_date,
          end_date: selectedContract.end_date,
          status: selectedContract.status,
          notes: selectedContract.notes || ''
        }
      );
      
      setContracts(contracts.map(c => c.id === selectedContract.id ? response.data : c));

      await fetchContracts();

      setEditContractDialogOpen(false);
      showSnackbar('Vertrag erfolgreich aktualisiert', "success");
      fetchProjectDetails();
    } catch (error) {
      console.error('Error updating contract:', error);
      showSnackbar(`Fehler beim Aktualisieren des Vertrags: ${JSON.stringify(error.response?.data) || error.message}`, "error");
    }
  };

  const handleDeleteContract = async (id) => {
    if (!window.confirm('Möchten Sie diesen Vertrag wirklich löschen?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/contracts/${id}/`);
      setContracts(contracts.filter(c => c.id !== id));
      showSnackbar('Vertrag erfolgreich gelöscht', "success");
      fetchProjectDetails();
    } catch (error) {
      console.error('Error deleting contract:', error);
      showSnackbar('Fehler beim Löschen des Vertrags', "error");
    }
  };

  const handleDownloadContract = (contractFileUrl) => {
    window.open(contractFileUrl, '_blank');
  };


    const handleContractInputChange = (event) => {
      const { name, value, files } = event.target;
      setNewContract(prev => ({
        ...prev,
        [name]: files ? files[0] : value
      }));
    };
  
    const handleEditContractInputChange = (event) => {
      const { name, value, files } = event.target;
      setSelectedContract(prev => ({
        ...prev,
        [name]: files ? files[0] : value
      }));
    };
  
    const handleCreateContract = async () => {
      const validAssignments = newContract.consultant_assignments.filter(
        a => a.consultant && a.hours > 0 && a.rate > 0
      );
  
      if (validAssignments.length === 0) {
        showSnackbar("Mindestens ein Berater mit Stunden und Rate ist erforderlich", "error");
        return;
      }
  
      if (dayjs(newContract.end_date).isBefore(dayjs(newContract.start_date))) {
        showSnackbar('Enddatum muss nach Startdatum liegen', "error");
        return;
      }
  
      try {
        const response = await axiosInstance.post('/contracts/', {
          title: newContract.title,
          project: projectId,
          consultant_assignments: validAssignments,
          start_date: newContract.start_date,
          end_date: newContract.end_date,
          status: newContract.status,
          notes: newContract.notes || ''
        });
        
        setContracts([response.data, ...contracts]);
        setCreateContractDialogOpen(false);
        
        setNewContract({
          title: '',
          project: projectId,
          consultant_assignments: [{ consultant: '', rate: 0, hours: 0 }],
          start_date: dayjs().format('YYYY-MM-DD'),
          end_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
          status: 'Draft',
          notes: '',
          contract_file: null
        });
  
        showSnackbar('Vertrag erfolgreich erstellt', "success");
        fetchProjectDetails();
      } catch (error) {
        console.error('Error creating contract:', error);
        showSnackbar(`Fehler beim Erstellen des Vertrags: ${JSON.stringify(error.response?.data) || error.message}`, "error");
      }
    };





  const handleDownloadAttachment = async (file) => {
    const response = await axiosInstance.get(`/attachments/${file.id}/download/`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDeleteAttachment = async (id) => {
    console.log(id);
    if (!window.confirm('Möchten Sie die Datei wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/attachments/${id}/`);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
  };

  const handleUploadAttachment = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedContract?.id) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('object_type', 'contract');
    formData.append('object_id', selectedContract.id);

    setUploading(true);
    try {
      await axiosInstance.post('/attachments/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showSnackbar('Datei erfolgreich hochgeladen', 'success');
      fetchAttachments(selectedContract.id);
    } catch (error) {
      console.error('Error uploading attachment:', error);
      showSnackbar('Fehler beim Hochladen der Datei', 'error');
    } finally {
      setUploading(false);
      // Reset so the same file can be re-uploaded if needed
      event.target.value = '';
    }
  };

  const fileIconMap = {
    image: <ImageIcon fontSize="small" />,
    pdf: <PictureAsPdfIcon fontSize="small" />,
    text: <DescriptionIcon fontSize="small" />,
    excel: <TableChartIcon fontSize="small" />,
    word: <ArticleIcon fontSize="small" />,
    file: <InsertDriveFileIcon fontSize="small" />,
  };

// Separate Komponente für jede TextField-Zelle
const EditableNumberCell = ({ value, onUpdate, min = 0, step = 0.01, width = 120 }) => {
  const [localValue, setLocalValue] = React.useState(value || '');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleBlur = () => {
    const numValue = parseFloat(localValue) || 0;
    onUpdate(numValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  return (
    <TextField
      ref={inputRef}
      type="number"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyPress={handleKeyPress}
      size="small"
      inputProps={{ min, step }}
      sx={{ width }}
    />
  );
};

const ConsultantAssignmentSection = ({ assignments, isEdit = false }) => (
  <Grid size={{ xs: 12}}>
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Beraterzuweisung</Typography>
        <Button
          startIcon={<PersonAddIcon />}
          onClick={() => addConsultantAssignment(isEdit)}
          size="small"
        >
          Berater hinzufügen
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Berater</TableCell>
              <TableCell align="right">Stundenrate (€)</TableCell>
              <TableCell align="right">Stunden</TableCell>
              <TableCell align="right">Gesamt</TableCell>
              <TableCell align="center">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.map((assignment, index) => (
              <TableRow key={`${assignment.consultant}-${index}`}>
                <TableCell>
                  <FormControl fullWidth size="small" required>
                    <Select
                      value={assignment.consultant || ''}
                      onChange={(e) => updateConsultantAssignment(index, 'consultant', e.target.value, isEdit)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>Berater auswählen</MenuItem>
                      {consultants.map(consultant => (
                        <MenuItem key={consultant.id} value={consultant.id}>
                          {consultant.first_name && consultant.last_name
                            ? `${consultant.first_name} ${consultant.last_name}`
                            : consultant.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="right">
                  <EditableNumberCell
                    value={assignment.rate}
                    onUpdate={(newValue) => updateConsultantAssignment(index, 'rate', newValue, isEdit)}
                    step={1}
                    width={120}
                  />
                </TableCell>
                <TableCell align="right">
                  <EditableNumberCell
                    value={assignment.hours}
                    onUpdate={(newValue) => updateConsultantAssignment(index, 'hours', newValue, isEdit)}
                    step={0.5}
                    width={100}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency((assignment.rate || 0) * (assignment.hours || 0))}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    onClick={() => removeConsultantAssignment(index, isEdit)}
                    color="error"
                    size="small"
                    disabled={assignments.length === 1}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3} align="right">
                <Typography variant="subtitle1" fontWeight="bold">Gesamtbetrag:</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  {formatCurrency(assignments.reduce((sum, a) => sum + ((a.rate || 0) * (a.hours || 0)), 0))}
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} align="right">
                <Typography variant="subtitle1" fontWeight="bold">Gesamtstunden:</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  {assignments.reduce((sum, a) => sum + Number(a.hours || 0), 0).toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Grid>
);

    // Contract Assignment Handlers
  const addConsultantAssignment = (isEdit = false) => {
    if (isEdit) {
      setSelectedContract(prev => ({
        ...prev,
        consultant_assignments: [
          ...prev.consultant_assignments,
          { consultant: '', rate: 0, hours: 0 }
        ]
      }));
    } else {
      setNewContract(prev => ({
        ...prev,
        consultant_assignments: [
          ...prev.consultant_assignments,
          { consultant: '', rate: 0, hours: 0 }
        ]
      }));
    }
  };

  const removeConsultantAssignment = (index, isEdit = false) => {
    if (isEdit) {
      setSelectedContract(prev => ({
        ...prev,
        consultant_assignments: prev.consultant_assignments.filter((_, i) => i !== index)
      }));
    } else {
      setNewContract(prev => ({
        ...prev,
        consultant_assignments: prev.consultant_assignments.filter((_, i) => i !== index)
      }));
    }
  };

  const updateConsultantAssignment = (index, field, value, isEdit = false) => {
    if (isEdit) {
      setSelectedContract(prev => ({
        ...prev,
        consultant_assignments: prev.consultant_assignments.map((assignment, i) =>
          i === index ? { ...assignment, [field]: value } : assignment
        )
      }));
    } else {
      setNewContract(prev => ({
        ...prev,
        consultant_assignments: prev.consultant_assignments.map((assignment, i) =>
          i === index ? { ...assignment, [field]: value } : assignment
        )
      }));
    }
  };    

  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { accountId } = useParams();
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentContractTab, setCurrentContractTab] = useState(0);


  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axiosInstance.get('/me/');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };


    const fetchAttachments = async (contractId) => {
      try {
        const response = await axiosInstance.get('/attachments/', {
          params: {
            object_type: 'contract',
            object_id: contractId,
          },
        });
        console.log("Attachments:");
        console.log(response);
        setAttachments(response.data);
      } catch (error) {
        console.error('Error fetching attachments', error);
      }
    };

  // Aktualisiert Projektfelder (InlineEdit, Phasenband, Verlust-Dialog)
  const patchProject = async (data) => {
    try {
      const response = await axiosInstance.patch(`/projects/${projectId}/`, data);
      setProject(response.data);
      showSnackbar('Erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating project:', error);
      showSnackbar('Fehler beim Aktualisieren', 'error');
      throw error; // Wichtig: Fehler weitergeben, damit InlineEdit weiß, dass es fehlgeschlagen ist
    }
  };
  const handleFieldUpdate = (field, value) => patchProject({ [field]: value });

  const handleContractTabChange = (event, newValue) => {
    setCurrentContractTab(newValue);
  };

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
    fetchProjectDetails();
    fetchConsultants();
    fetchTimeEntries();
    fetchContracts();
  }, [projectId]);

    
  const fetchProjectDetails = async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/`);
      setProject(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching project:", error);
      showSnackbar('Error fetching project', "error");
      setLoading(false);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await axiosInstance.get(`/time-entries/?project_id=${projectId}`);
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await axiosInstance.get(`/contracts/?project=${projectId}`);
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };


  const contactColumns = [
    { field: 'id', headerName: 'ID', width: 50 },
    { field: 'salutation', headerName: 'Salutation', width: 100 },
    { field: 'first_name', headerName: 'First Name', width: 150 },
    { field: 'last_name', headerName: 'Last Name', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 200 },
    { field: 'position', headerName: 'Position', width: 150 },

    { 
      field: 'priority', 
      headerName: 'Priority', 
      width: 70,
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >

        </Box>
      ),
    },
  ];

  const isCancelled = !!projectLost && project?.status === projectLost.key;

  const renderHeader = () => {
    if (!project) return null;
    const facts = [
      { label: 'Zeitraum', value: `${dayjs(project.start_date).format('DD.MM.YYYY')} – ${dayjs(project.end_date).format('DD.MM.YYYY')}` },
      { label: 'Verbleibende Stunden', value: project.remaining_hours ?? '—' },
      { label: 'Verträge', value: contracts.length },
    ];

    return (
      <Item>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{project.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{project.account_name}</Typography>
              {project.account && (
                <Tooltip title="Unternehmen öffnen" arrow placement="top">
                  <IconButton size="small" color="primary" onClick={handleOpenAccount}><OpenInNewOutlinedIcon sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {project.consultants?.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">Berater</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {project.consultants.map((c, i) => (
                    <Tooltip key={i} title={c} arrow placement="top">
                      <Avatar sx={{ width: 30, height: 30, fontSize: '0.72rem', fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark' }}>{getInitials(c)}</Avatar>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}
            <IconButton aria-label="Weitere Aktionen" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreHorizIcon />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem
                onClick={() => { setMenuAnchor(null); setLostDialogOpen(true); }}
                disabled={isCancelled || !projectLost} sx={{ color: 'error.main' }}
              >
                Als abgebrochen markieren
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: 3, md: 5 }, flexWrap: 'wrap', mt: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {facts.map((f) => (
            <Box key={f.label}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>{f.label}</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{f.value}</Typography>
            </Box>
          ))}
        </Box>

        {!project.primary_contact_detail && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            <Typography variant="body2" color="text.secondary">Kein primärer Kontakt gesetzt.</Typography>
          </Box>
        )}
      </Item>
    );
  };

  const renderDetailsTab = () => {
    return (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Item>
            <Typography variant="h6" sx={{ mb: 3 }}>Projekt-Informationen</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <InlineEdit value={project?.name} onSave={(value) => handleFieldUpdate('name', value)} />
              </Box>
               
              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Unternehmen</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                    <Typography variant="inherit">{project?.account_name}</Typography>
                      {project?.account && (
                        <Tooltip title="Unternehmensdetails öffnen" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleOpenAccount}
                          >
                            <OpenInNewOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Primärer Kontakt</Typography>
                <Typography variant="inherit">{project?.primary_contact_detail?.first_name} {project?.primary_contact_detail?.last_name}</Typography>
              </Box>
              

              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Startdatum</Typography>
                <InlineEdit value={project?.start_date} onSave={(value) => handleFieldUpdate('start_date', value)}  type='date'/>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Enddatum</Typography>
                <InlineEdit value={project?.end_date} onSave={(value) => handleFieldUpdate('end_date', value)}  type='date'/>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Berater</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {project?.consultants?.map((consultantName, idx) => (
                      <Tooltip key={idx} title={`${consultantName}`} arrow placement="top">
                        <Avatar
                          key={idx}
                          sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}
                        >
                          {getInitials(consultantName)}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </Box>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Verbleibende Stunden</Typography>
                <Typography variant="inherit">{project?.remaining_hours}</Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Beschreibung</Typography>
                <InlineEdit value={project?.description} onSave={(value) => handleFieldUpdate('description', value)} multiline={true} />
              </Box>

            </Box>
          </Item>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Item>

            {/* Interaction Timeline */}
            <InteractionTimeline relatedType="project" relatedId={projectId} />
         
          </Item>
        </Grid>
      </Grid>
    );
  };

  const renderTimeEntriesTab = () => {
      return (
          <Item>
            {/* Zeiterfassung */}
            <Typography variant="h6" sx={{ mb: 2 }}>Zeiterfassung</Typography>
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'action.hover', border: 'none', borderRadius: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Filter</Typography>
              <Grid container spacing={2} alignItems="center">

                {/* Berater – immer sichtbar */}
                <Grid size={{ xs: 12, md: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Berater</InputLabel>
                    <Select
                      value={timeEntryFilters.consultant}
                      onChange={(e) => setTimeEntryFilters(prev => ({ ...prev, consultant: e.target.value }))}
                      label="Berater"
                    >
                      <MenuItem value="">Alle</MenuItem>
                      {consultants.map(consultant => (
                        <MenuItem key={consultant.id} value={consultant.id}>
                          {consultant.first_name && consultant.last_name
                            ? `${consultant.first_name} ${consultant.last_name}`
                            : consultant.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Modus-Toggle */}
                <Grid size={{ xs: 12, md: 'auto' }}>
                  <Tabs
                    value={filterMode}
                    onChange={(_, val) => handleFilterModeChange(val)}
                    sx={{
                      minHeight: 36,
                      '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 2 },
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Tab label="Monat" value="month" />
                    <Tab label="Zeitraum" value="range" />
                  </Tabs>
                </Grid>

                {/* Monatsfilter */}
                {filterMode === 'month' && (
                  <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Monat</InputLabel>
                      <Select
                        value={timeEntryFilters.month}
                        onChange={(e) => setTimeEntryFilters(prev => ({ ...prev, month: e.target.value }))}
                        label="Monat"
                      >
                        <MenuItem value="">Alle</MenuItem>
                        {Array.from(
                          new Set(timeEntries.map(e => dayjs(e.date).format('YYYY-MM')))
                        )
                          .sort((a, b) => b.localeCompare(a))
                          .map(month => (
                            <MenuItem key={month} value={month}>
                              {dayjs(month).format('MMMM YYYY')}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Zeitraumfilter */}
                {filterMode === 'range' && (
                  <>
                    <Grid size={{ xs: 12, md: 1.5 }}>
                      <TextField
                        label="Startdatum"
                        type="date"
                        size="small"
                        fullWidth
                        value={timeEntryFilters.startDate}
                        onChange={(e) => setTimeEntryFilters(prev => ({ ...prev, startDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 1.5 }}>
                      <TextField
                        label="Enddatum"
                        type="date"
                        size="small"
                        fullWidth
                        value={timeEntryFilters.endDate}
                        onChange={(e) => setTimeEntryFilters(prev => ({ ...prev, endDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </>
                )}

                {/* Reset */}
                <Grid size={{ xs: 12, md: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleResetFilters}
                    disabled={!hasActiveFilters()}
                  >
                    Zurücksetzen
                  </Button>
                </Grid>

              </Grid>
            </Paper>

            {/* Header with Export Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6">
                  Zeiteinträge ({getFilteredTimeEntries().length}
                  {hasActiveFilters() && ` von ${timeEntries.length}`})
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                  {hasActiveFilters() && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Stunden (gefiltert):
                    </Typography>
                    <Typography variant="h8" color="primary">
                      {getFilteredTimeEntries().reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0).toFixed(2)}h
                    </Typography>
                  </Box>
                  )}
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Stunden (Gesamt):
                      </Typography>
                      <Typography variant="h8">
                        {timeEntries.reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0).toFixed(2)}h
                      </Typography>
                    </Box>
                </Box>
              </Box>
              {getFilteredTimeEntries().length > 0 && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<DescriptionIcon />}
                    onClick={handleExportTimeEntriesToCSV}
                    color="primary"
                  >
                    CSV Export
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleExportTimeEntriesToPDF}
                    color="primary"
                  >
                    PDF Export
                  </Button>
                </Box>
              )}
            </Box>

            {getFilteredTimeEntries().length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {hasActiveFilters() 
                    ? 'Keine Zeiteinträge zu den ausgewählten Filtern'
                    : 'Keine Zeiteinträge für dieses Projekt'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <DataGrid
                  rows={[...getFilteredTimeEntries()].sort((a, b) => new Date(b.date) - new Date(a.date))}
                  columns={[
                    {
                      field: 'actions',
                      headerName: 'Aktionen',
                      width: 100,
                      sortable: false,
                      renderCell: (params) => {
                        const isOwner = params.row.consultant === currentUser?.id;

                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Tooltip title={isOwner ? "Eintrag bearbeiten" : "Nur eigene Einträge bearbeiten"}>
                              <span>
                                <IconButton
                                  variant="action-edit"
                                  size="small"
                                  onClick={() => isOwner && handleOpenEditTimeEntryDialog(params.row)}
                                  disabled={!isOwner}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={isOwner ? "Eintrag löschen" : "Nur eigene Einträge löschen"}>
                              <span>
                                <IconButton
                                  variant="action-delete"
                                  size="small"
                                  onClick={() => isOwner && handleDeleteTimeEntry(params.row.id)}
                                  disabled={!isOwner}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        );
                      },
                    },
                    {
                      field: 'date',
                      headerName: 'Datum',
                      width: 130,
                      valueFormatter: (value) => formatDate(value),
                    },
                    {
                      field: 'consultant',
                      headerName: 'Berater',
                      width: 200,
                      renderCell: (params) => {
                        const consultant = consultants.find(c => c.id === params.value);
                        const name = consultant?.first_name && consultant?.last_name
                          ? `${consultant.first_name} ${consultant.last_name}`
                          : consultant?.email || 'Unbekannt';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}>
                              {getInitials(name)}
                            </Avatar>
                            {name}
                          </Box>
                        );
                      },
                    },
                    {
                      field: 'hours',
                      headerName: 'Stunden',
                      width: 120,
                      renderCell: (params) => (
                        <Chip label={`${params.value}h`} size="small" color="primary" variant="outlined" />
                      ),
                    },
                    {
                      field: 'description',
                      headerName: 'Beschreibung',
                      flex: 1,
                      renderCell: (params) => (
                        <Typography variant="body2" sx={{ whiteSpace: 'normal', py: 1 }}>
                          {params.value || '-'}
                        </Typography>
                      ),
                    },
                  ]}
                  getRowHeight={() => 'auto'}
                  getEstimatedRowHeight={() => 52}
                  getRowId={(row) => row.id}
                  autoHeight
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                  }}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      py: 1,          // vertikales Padding damit es nicht zu eng wirkt
                      alignItems: 'center',
                    },
                }}
                />
                </TableContainer>
            )}


            {/* Edit TimeEntry Dialog */}
            <Dialog open={editTimeEntryDialogOpen} onClose={handleCloseEditTimeEntryDialog} maxWidth="md" fullWidth>
              <DialogTitle>Zeiteintrag bearbeiten</DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>

                  {/* Read-only fields */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Projekt</Typography>
                    <Typography variant="body1">{selectedTimeEntry?.project_name || '—'}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">Vertrag</Typography>
                    <Typography variant="body1">{selectedTimeEntry?.contract_title || '—'}</Typography>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Vertragsstatus</Typography>
                    <Chip
                      label={selectedTimeEntry?.contract_status || '—'}
                      color={getStatusColor(selectedTimeEntry?.contract_status)}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">Berater</Typography>
                    <Typography variant="body1">{selectedTimeEntry?.consultant_name || '—'}</Typography>
                  </Grid>



                  {/* Divider */}
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>


                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Datum"
                      type="date"
                      value={selectedTimeEntry?.date || ''}
                      onChange={(e) => setSelectedTimeEntry(prev => ({ ...prev, date: e.target.value }))}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Stunden"
                      type="number"
                      inputProps={{ min: 0, step: 0.25 }}
                      value={selectedTimeEntry?.hours || ''}
                      onChange={(e) => setSelectedTimeEntry(prev => ({ ...prev, hours: e.target.value }))}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Beschreibung"
                      value={selectedTimeEntry?.description || ''}
                      onChange={(e) => setSelectedTimeEntry(prev => ({ ...prev, description: e.target.value }))}
                      fullWidth
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseEditTimeEntryDialog}>Abbrechen</Button>
                <Button
                  onClick={async () => {
                    await handleSaveTimeEntry(selectedTimeEntry.id, {
                      date: selectedTimeEntry.date,
                      hours: selectedTimeEntry.hours,
                      description: selectedTimeEntry.description,
                    });
                    handleCloseEditTimeEntryDialog();
                  }}
                  variant="contained"
                >
                  Speichern
                </Button>
              </DialogActions>
            </Dialog>

          </Item>
        )
      };



  const renderEditContractsTab = () => {
    return ( 
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="title"
                  label="Titel"
                  value={selectedContract?.title || '' }
                  onChange={handleEditContractInputChange}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={selectedContract?.status || 'Draft'}
                    onChange={handleEditContractInputChange}
                    label="Status"
                  >
                  {contractStatusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="start_date"
                  label="Startdatum"
                  type="date"
                  value={selectedContract?.start_date || ''}
                  onChange={handleEditContractInputChange}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="end_date"
                  label="Enddatum"
                  type="date"
                  value={selectedContract?.end_date || ''}
                  onChange={handleEditContractInputChange}
                  fullWidth
                  required
                />
              </Grid>

              <Grid size={{ xs: 12}}>
              {selectedContract && (
                <ConsultantAssignmentSection 
                  assignments={selectedContract.consultant_assignments} 
                  isEdit={true}
                />
              )}
              </Grid>

              <Grid size={{ xs: 12}}>
                <TextField
                  name="notes"
                  label="Notizen"
                  value={selectedContract?.notes || ''}
                  onChange={handleEditContractInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
    );
  };

    const renderContractsAttachments = () => {
      return (
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography>
              Anzahl Dateien: {attachments.length}
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
              size="small"
              disabled={uploading}
            >
              {uploading ? 'Wird hochgeladen…' : 'Datei hochladen'}
              <input
                type="file"
                hidden
                //accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={handleUploadAttachment}
              />
            </Button>
          </Box>
  
          {attachments.map((file) => (
            <Box
              key={file.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* Linke Seite: Icon + Dateiinformationen */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  
  
                <Box>
                  <Tooltip title={file.name}>
                    <Typography variant="body2" fontWeight="medium">
                      {truncateFileName(file.name, 64)}
                    </Typography>
                  </Tooltip>
                  {file.description && (
                    <Typography variant="body2" fontWeight="medium">
                      Beschreibung: {truncateFileName(file.description, 64)}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title={getMimeType(file.name)}>
                      {fileIconMap[getFileIcon(getMimeType(file.name))]}
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {getMimeType(file.name)} • {formatBytes(file.size)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
  
              {/* Rechte Seite: Aktionen */}
              <Box>
                <Tooltip title="Öffnen">
                  <IconButton
                    variant="action-edit"
                    size="small"
                    onClick={async () => {
                      const response = await axiosInstance.get(`/attachments/${file.id}/download/`, {
                        responseType: 'blob',
                      });
                      const mimeType = response.headers['content-type']?.split(';')[0] || 'application/octet-stream';
                      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
                      const a = document.createElement('a');
                      a.href = blobUrl;
                      a.target = '_blank';
                      a.rel = 'noopener';
                      a.download = file.name;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10_000);
                    }}
                  >
                    <DescriptionIcon />
                  </IconButton>
                </Tooltip>
  
                <Tooltip title="Herunterladen">
                  <IconButton
                    variant="action-edit"
                    size="small"
                    onClick={() => handleDownloadAttachment(file)}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
  
                <Tooltip title="Löschen">
                  <IconButton
                    variant="action-delete"
                    size="small"
                    onClick={() => handleDeleteAttachment(file.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
  
              </Box>
            </Box>
          ))}
        </DialogContent>
      );
    };



  const renderContractsTab = () => {
      return (
          <Item>
            <Paper elevation={0} sx={{ p: 0, mb: 0, bgcolor: 'transparent', border: 'none' }}>
         {/* Verträge */}
          <Box sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Verträge ({contracts.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateContractDialogOpen(true)}
              >
                Vertrag erstellen
              </Button>
            </Box>

            {contracts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Keine Verträge verfügbar
                </Typography>
              </Box>
            ) : (
              <Box>
                {contracts.map((contract) => (
                  <Accordion
                    key={contract.id}
                    expanded={expandedContract === contract.id}
                    onChange={handleAccordionChange(contract.id)}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        flexDirection: 'row-reverse',
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          transform: 'rotate(-90deg)',
                          marginRight: 1
                        },
                        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                          transform: 'rotate(0deg)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                            <Typography variant="h6">
                              {contract.title || 'Vertrag'}
                            </Typography>

                            {(() => {
                              const contractStatus = contractStatusOptions.find(opt => opt.value === contract.status);
                              const c = getStatusColor(contractStatus?.value || 'default');
                              return (
                                <Chip
                                  label={contractStatus?.label || contract.status}
                                  size="small"
                                  sx={{ bgcolor: alpha(c, 0.13), color: c, fontWeight: 600 }}
                                />
                              );
                            })()}
                          </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            <CalendarTodayIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                            {formatDate(contract.start_date)} - {formatDate(contract.end_date)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              variant="action-edit" // get style from theme.js
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditContractDialog(contract);
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {/* Vertrag löschen ist serverseitig Managern vorbehalten (Security S3) */}
                            {currentUser?.is_staff && (
                              <IconButton
                               variant="action-delete" // get style from theme.js
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteContract(contract.id);
                                }}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {contract.consultant_assignments && contract.consultant_assignments.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            Beraterzuweisung:
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Berater</TableCell>
                                  <TableCell align="right">Stunden</TableCell>
                                  <TableCell align="right">Stundenrate</TableCell>
                                  <TableCell align="right">Gesamt</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {contract.consultant_assignments.map((assignment, idx) => {
                                  const consultant = consultants.find(c => c.id === assignment.consultant);
                                  const consultantName = consultant?.first_name && consultant?.last_name
                                    ? `${consultant.first_name} ${consultant.last_name}`
                                    : consultant?.email || 'Unbekannt';
                                  
                                  return (
                                    <TableRow key={idx}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                                            {getInitials(consultantName)}
                                          </Avatar>
                                          {consultantName}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">{assignment.hours}h</TableCell>
                                      <TableCell align="right">
                                        {formatCurrency(assignment.rate)}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" color="primary" fontWeight="bold">
                                          {formatCurrency(assignment.hours * assignment.rate)}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                <TableRow>
                                  <TableCell colSpan={3} align="right">
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      Gesamtkosten:
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                      {formatCurrency(
                                        contract.consultant_assignments.reduce(
                                          (sum, a) => sum + (a.hours * a.rate), 
                                          0
                                        )
                                      )}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}

                      {contract.notes && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            Notizen:
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {contract.notes}
                          </Typography>
                        </Box>
                      )}

                      {contract.contract_file_url && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDownloadContract(contract.contract_file_url)}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                          <Typography variant="caption" color="text.secondary">
                            Vertragsdokument herunterladen
                          </Typography>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
      </Paper>

      {/* Create Contract Dialog */}
      <Dialog open={createContractDialogOpen} onClose={() => setCreateContractDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neuen Vertrag erstellen</DialogTitle>
        <DialogContent>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>


            <Grid size={{ xs: 12, md: 6}}>
              <TextField
                name="title"
                label="Titel"
                value={newContract.title || '' }
                onChange={handleContractInputChange}
                fullWidth
              />
            </Grid>


            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={newContract.status}
                  onChange={handleContractInputChange}
                  label="Status"
                >
                  {contractStatusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}

                </Select>
              </FormControl>
            </Grid>

           <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="start_date"
                label="Startdatum"
                type="date"
                value={newContract.start_date}
                onChange={handleContractInputChange}
                fullWidth
                required
              />
            </Grid>
      
           <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="end_date"
                label="Enddatum"
                type="date"
                value={newContract.end_date}
                onChange={handleContractInputChange}
                fullWidth
                required
              />
            </Grid>

        <Grid size={12}>
          <ConsultantAssignmentSection 
            assignments={newContract.consultant_assignments} 
            isEdit={false}
          />
        </Grid>

            <Grid size={12}>
              <TextField
                name="notes"
                label="Notizen"
                value={newContract.notes}
                onChange={handleContractInputChange}
                multiline
                rows={3}
                fullWidth
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateContractDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleCreateContract}
            variant="contained"
            color="primary"
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contract Dialog */}
      <Dialog open={editContractDialogOpen} onClose={() => setEditContractDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vertrag bearbeiten</DialogTitle>


        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={currentContractTab} onChange={handleContractTabChange}>
            <Tab label="Vertragsdaten" />
            <Tab label={`Dateien (${attachments.length})`} />
          </Tabs>
        </Box>


        {currentContractTab === 0 && renderEditContractsTab()}
        {currentContractTab === 1 && renderContractsAttachments()}


        <DialogActions>
          <Button onClick={() => setEditContractDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleSaveContract}
            variant="contained"
            color="primary"
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Item>
  );
      };

  
      

  // Phasenband (B3): Checkliste der aktuellen Phase aus der Konfiguration
  const curPhase = project ? projectPhases.find((p) => p.key === project.status) : null;
  const doneSet = new Set((project && curPhase && project.checklist_state && project.checklist_state[curPhase.key]) || []);
  const checklistItems = curPhase ? curPhase.checklist.map((item) => ({ label: item, done: doneSet.has(item) })) : [];
  const toggleChecklistItem = (label) => {
    if (!curPhase) return;
    const next = new Set(doneSet);
    next.has(label) ? next.delete(label) : next.add(label);
    patchProject({ checklist_state: { ...(project.checklist_state || {}), [curPhase.key]: [...next] } });
  };

  if (loading) return <DetailSkeleton />;

  return (
    // Kopf + Phasenband immer sichtbar; darunter Tabs (Alexanders Wunsch):
    // Übersicht · Verträge · Zeiterfassung — mit Zählern an den Reitern.
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {renderHeader()}
      {project && (
        <MilestoneTracker
          phases={projectPhases}
          rampFamily="support"
          currentKey={project.status}
          lost={projectLost}
          successKey={projectPhases[projectPhases.length - 1]?.key}
          sinceDate={project.phase_changed_at}
          lostReason={project.lost_reason}
          checklistItems={checklistItems}
          onToggleChecklistItem={toggleChecklistItem}
          onChangePhase={(key) => handleFieldUpdate('status', key)}
        />
      )}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)}>
          <Tab label="Übersicht" />
          <TabWithCounter label="Verträge" count={contracts.length} />
          <TabWithCounter label="Zeiterfassung" count={timeEntries.length} />
        </Tabs>
      </Box>
      {activeTab === 0 && renderDetailsTab()}
      {activeTab === 1 && renderContractsTab()}
      {activeTab === 2 && renderTimeEntriesTab()}
      <LostReasonDialog
        open={lostDialogOpen}
        onClose={() => setLostDialogOpen(false)}
        title="Projekt als abgebrochen markieren"
        onConfirm={(reason) => { setLostDialogOpen(false); patchProject({ status: projectLost?.key, lost_reason: reason }); }}
      />
    </Box>
  );
};

export default ProjectDetails;